import { Space } from 'antd';
import { forwardRef } from 'react';
import { t } from 'ttag';
import CustomInput from '../inputs/customInput';
import CustomSelect from '../customSelect';
import { arrowDownGreyIcon } from './assets';
import './styles.scss';
import type { SearchComponentProps } from './types';
import { useSearchComponent } from './useSearchComponent';

const CustomSelectAny: any = CustomSelect;
const EMPTY_STYLE = {};

/**
 * Search bar pairing a value input with a select naming the field being searched. Forwards its ref through useSearchComponent, renders a CustomSelect of physician options when searchByValue is physicianId and a clearable CustomInput otherwise, and offers patient name, study ID, patient ID and device ID as the default search-by options.
 *
 * @param props - Current search-by value and option lists, sizing and dropdown settings, disabled flag and the search change handlers.
 * @param ref - Forwarded ref exposing the search state to the parent.
 * @returns The compact search input and dropdown pair.
 */
const SearchComponent = forwardRef((props: SearchComponentProps, ref) => {
  const {
    disabled = false,
    showSearch = false,
    inputType = 'text',
    popupMatchSelectWidth = true,
    dropdownStyle = EMPTY_STYLE,
    placement = 'bottomLeft',
    dropdownWidth = 145,
    inputWidth = 205,
  } = props;
  const { state, onChangeSelect, onChangeSearchSelect, onChangeSearch } = useSearchComponent(
    props,
    ref,
  );

  const options = [
    {
      value: 'patientName',
      label: 'Patient name',
      title: 'by Patient name',
    },
    {
      value: 'studyFid',
      label: 'Study ID',
      title: 'by Study ID',
    },
    {
      value: 'patientId',
      label: 'Patient ID',
      title: 'by Patient ID',
    },
    {
      value: 'deviceId',
      label: 'Device ID',
      title: 'by Device ID',
    },
  ];

  return (
    <Space.Compact>
      {props.searchByValue === 'physicianId' ? (
        <CustomSelect
          id="physician-select"
          disabled={disabled}
          className="search-select"
          placeholder={`${t`Search`}...`}
          showArrow={false}
          name="physician"
          onChange={onChangeSearchSelect}
          value={state.searchValue}
          options={props.optionsSelectValue}
          size="middle"
          style={{ minWidth: 175 }}
          isSelectOnlyClearIcon
          aria-label={t`Search by physician`}
        />
      ) : (
        <CustomInput
          id="search-input"
          name="searchValue"
          aria-label={t`Search`}
          disabled={disabled}
          className="search-input"
          allowClear
          size="middle"
          value={state.searchValue ?? undefined}
          placeholder={`${t`Search`}...`}
          style={{ width: inputWidth }}
          onChange={onChangeSearch}
          type={inputType as any}
        />
      )}
      <CustomSelectAny
        id="search-by-select"
        disabled={disabled}
        className="option-search"
        onChange={onChangeSelect}
        value={props.searchByValue}
        options={props.options || options}
        size="middle"
        style={{ width: dropdownWidth }}
        allowClear={false}
        showSearch={showSearch}
        popupMatchSelectWidth={popupMatchSelectWidth}
        dropdownStyle={dropdownStyle}
        suffixIcon={<img src={arrowDownGreyIcon} alt="arrow icon" />}
        placement={placement as any}
        aria-label={t`Search by`}
      />
    </Space.Compact>
  );
});

export default SearchComponent;
